/*
 * Copyright (c) GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import { RunType } from 'models/viewModels';

export enum VisualizationType {
  RaceOrderTable = 'raceOrderTable',
  TrackViewMap = 'trackViewMap',
  Weather = 'weather',
  WindyWeather = 'windyWeather',
  WeatherMap = 'weatherMap',
  LapDeltaTable = 'lapDeltaTable',
  LapTimesGraph = 'lapTimesGraph',
  LapTimesTable = 'lapTimesTable',
  RunningOrderTable = 'runningOrderTable',
  PitRoadSummaryTable = 'pitRoadSummaryTable',
  PitStrategyTable = 'pitStrategyTable',
  PitRoadDurationTable = 'pitRoadDurationTable',
  PitRoadDurationGraph = 'pitRoadDurationGraph',
  PitRoadDurationJackGraph = 'pitRoadDurationJackGraph',
  PitRoadCrewTable = 'pitRoadCrewTable',
  PitRoadCrewAnalysisGraph = 'pitRoadCrewAnalysisGraph',
  PitRoadCycleTimeTable = 'pitRoadCycleTimeTable',
  PitRoadCycleTimeGraph = 'pitRoadCycleTimeGraph',
  TireAgeTimeline = 'tireAgeTimeline',
  TrackTemperature = 'trackTemperature',
  IndyWindWeatherGraph = 'indyWindWeatherGraph',
  IndyWindWeatherMap = 'indyWindWeatherMap',
  SessionInfo = 'sessionInfo',
  RaceProgression = 'raceProgression',
  RankAnalysisTable = 'rankAnalysisTable',
  RankAnalysisReport = 'rankAnalysisReport',
  FastestLapsTable = 'fastestLapsTable',
  AudioTranscription = 'audioTranscription',
  VehicleGPSQuality = 'vehicleGPSQuality',
  CompetitorGroupsDock = 'competitorGroupsDock',
  ManufacturerPoints = 'manufacturerPoints',
  OwnerPoints = 'ownerPoints',
  DriverPoints = 'driverPoints',
  BobPockrassTwitterFeed = 'bobPockrassTwitterFeed',
  VehicleAudioActivityLevel = 'vehicleAudioActivityLevel',
  DriverPositions = 'driverPositions',
  AlertNotificationLog = 'alertNotificationLog',
}

export type VisualizationTypeOption = {
  type?: VisualizationType;
  label: string;
  isHeader?: true;
  isHiddenOnProduction?: true;
  isHidden?: true;
  combinedViewOnly?: true;
  mobileMenuOrder?: number;
};

// TODO Wish this was a map of VisualizationType to VisualizationTypeOptions for O(1) lookups, could use .values() when we need the array
// Would need a type for the headers, but at that point, might as well rework this to delineate headers vs visualizations
// if headers are removed from this list, will need to have an association of Visualizations to their parent header
export const allVisualizationTypeOptions: VisualizationTypeOption[] = [
  { label: 'Standard', isHeader: true, combinedViewOnly: true },
  {
    label: 'Session Info',
    type: VisualizationType.SessionInfo,
    combinedViewOnly: true,
  },
  {
    label: 'Race Info',
    type: VisualizationType.RaceProgression,
    combinedViewOnly: true,
  },
  {
    label: 'Lap Times Graph',
    type: VisualizationType.LapTimesGraph,
    combinedViewOnly: true,
  },
  {
    label: 'Lap Times Table',
    type: VisualizationType.LapTimesTable,
    combinedViewOnly: true,
    mobileMenuOrder: 2,
  },
  {
    label: 'Lap Delta Table',
    type: VisualizationType.LapDeltaTable,
  },
  {
    label: 'Race Order Table',
    type: VisualizationType.RaceOrderTable,
    combinedViewOnly: true,
    mobileMenuOrder: 1,
  },
  {
    label: 'Running Order Table',
    type: VisualizationType.RunningOrderTable,
    combinedViewOnly: true,
  },
  {
    label: 'Driver Positions',
    type: VisualizationType.DriverPositions,
  },
  { label: 'Pit Road', isHeader: true },
  // TODO Remove isHidden once these pitRoadAnalysis stream is on new stack and not dependent on SMT
  { label: 'Cycle Time Graph', type: VisualizationType.PitRoadCycleTimeGraph, isHidden: true },
  { label: 'Cycle Time Table', type: VisualizationType.PitRoadCycleTimeTable, isHidden: true },
  { label: 'Duration Graph', type: VisualizationType.PitRoadDurationGraph, isHidden: true },
  { label: 'Duration Table', type: VisualizationType.PitRoadDurationTable, isHidden: true },
  { label: 'Summary Table', type: VisualizationType.PitRoadSummaryTable },
  { label: 'Pit Strategy Table', type: VisualizationType.PitStrategyTable },
  { label: 'Tire Age Timeline', type: VisualizationType.TireAgeTimeline },
  { label: 'Track', isHeader: true },
  { label: 'Track Temperature', type: VisualizationType.TrackTemperature },
  { label: 'Track View Map', type: VisualizationType.TrackViewMap },
  { label: 'Weather', isHeader: true },
  { label: 'Forecast', type: VisualizationType.Weather },
  { label: 'Radar Map', type: VisualizationType.WeatherMap },
  { label: 'Indy Wind Weather', type: VisualizationType.IndyWindWeatherGraph },
  { label: 'Indy Wind Station Map', type: VisualizationType.IndyWindWeatherMap },
  { label: 'WindyWeather', type: VisualizationType.WindyWeather },
  { label: 'Reports', isHeader: true },
  { label: 'Rank Analysis Report', type: VisualizationType.RankAnalysisReport },
  { label: 'Fastest 10-20-30', type: VisualizationType.FastestLapsTable },
  { label: 'Manufacturer Points', type: VisualizationType.ManufacturerPoints },
  { label: 'Owner Points', type: VisualizationType.OwnerPoints },
  { label: 'Driver Points', type: VisualizationType.DriverPoints },
  { label: 'Admin', isHeader: true },
  { label: 'Vehicle GPS Quality', type: VisualizationType.VehicleGPSQuality },
  { label: 'Vehicle Audio Activity Level', type: VisualizationType.VehicleAudioActivityLevel },
  { label: 'Miscellaneous', isHeader: true },
  {
    label: 'Audio Transcription',
    type: VisualizationType.AudioTranscription,
  },
  {
    label: 'Global Competitor Groups',
    type: VisualizationType.CompetitorGroupsDock,
    combinedViewOnly: true,
    mobileMenuOrder: 3,
  },
  {
    label: 'Bob Pockrass Twitter Feed',
    type: VisualizationType.BobPockrassTwitterFeed,
  },
  { label: 'Alert Notification Log', type: VisualizationType.AlertNotificationLog },
];

export const qualifyingVisualizationTypeOptions: VisualizationTypeOption[] = allVisualizationTypeOptions.map((item) =>
  item.type !== VisualizationType.RaceOrderTable
    ? item
    : { label: 'Qualifying Order Table', type: VisualizationType.RaceOrderTable, header: false }
);

export const mobileVisualizations = allVisualizationTypeOptions
  .filter((vis) => vis.mobileMenuOrder !== undefined)
  .sort((a, b) => {
    return (a.mobileMenuOrder || 0) - (b.mobileMenuOrder || 0);
  });

export const getVisualizationLabel = (type: VisualizationType): string => {
  const option = allVisualizationTypeOptions.find((option) => option.type === type);
  return option?.label || '';
};

export const getVisualizationLabelBySessionType = (
  type: VisualizationType,
  sessionType: RunType | undefined
): string => {
  const isQualifying = sessionType === RunType.QUALIFYING;
  const options = isQualifying ? qualifyingVisualizationTypeOptions : allVisualizationTypeOptions;
  const option = options.find((option) => option.type === type);
  return option?.label || '';
};

export const indyDefaultVisualizations = new Set<VisualizationType>([
  VisualizationType.LapTimesGraph,
  VisualizationType.LapTimesTable,
  VisualizationType.RaceOrderTable,
  VisualizationType.RunningOrderTable,
  VisualizationType.DriverPositions,
  VisualizationType.CompetitorGroupsDock,
  VisualizationType.IndyWindWeatherGraph,
  VisualizationType.IndyWindWeatherMap,
  VisualizationType.RankAnalysisReport,
  VisualizationType.FastestLapsTable,
  VisualizationType.WindyWeather,
]);

export const strategyVisualizations = new Set<VisualizationType>([VisualizationType.PitStrategyTable]);

/**
 * Insert GM-only / Feature Preview / other restricted visualizations here
 * TODO remove when all SacntioningBodies/Series have a DashboardType and
 * define their own visualization sets
 */
export const restrictedVisualizations = new Set<VisualizationType>([VisualizationType.PitStrategyTable]);
